import { useEffect, useRef, useState } from "react";
import { useOpenPropertyCheck, type OpenPropertyAccess } from "./useOpenPropertyCheck";
import { hasUnsavedWork, runWhenUnblocked } from "../navigation/history";
import { isAdminPage, type PageName } from "../navigation/pages";
import { setPageConnection } from "../services/loadedConnection";
import { onOpenPropertyCheckRequested } from "../services/openPropertyEvents";
import { forgetPropertyCaches } from "../services/propertyCaches";

interface OpenPropertyWatchOptions {
    /**
     * The property the shell loaded its pages for (`useDashboardInit`);
     * undefined while it is not known, and with no property open.
     */
    loadedConnectionId: number | undefined;
    /** Moves whenever the pages are loaded again for another property. */
    connectionVersion: number;
    /** The page on screen; null while the shell shows none. */
    page: PageName | null;
    /**
     * Whether the page on screen shows something of the open property. Every
     * page does but the admin consoles and the account page's own sections —
     * a person's tokens and AI-agent switch (`accountSectionFollowsOpenProperty`)
     * — so it is read as true unless the shell says otherwise.
     */
    showsProperty?: boolean;
    /**
     * How the caller reached the property when the pages were loaded for it
     * (`useDashboardInit`): their role and a viewer's kind of grant.
     */
    loadedAccess?: OpenPropertyAccess;
    /**
     * Called when the property still open answers with another access than
     * `loadedAccess`, read as the shell gates by it (`gatingReadingOf`) — an
     * owner narrowed this person to the front desk, or gave them full access,
     * while the tab was open. The shell takes the new access the way it takes
     * a property switch, so a front desk left with the analytics on screen
     * loses them without a reload. Where the page holds unsaved work it is
     * called only once the discard guard has been answered.
     */
    onAccessChanged?: () => void;
}

export interface OpenPropertyWatch {
    /** Another property is open now than the one the tab was loaded for. */
    changed: boolean;
    /**
     * …and the page on screen shows a property, so the shell puts
     * `PropertyOpenedElsewhereNotice` in its place. The admin consoles read
     * every tenant and never the open property, and stay on screen.
     */
    openElsewhere: boolean;
    /** The property the service named as open; null for none. */
    openConnectionId: number | null | undefined;
    /**
     * The page under the notice held unsaved work when the tab turned out to
     * show a property no longer open, so it is kept, hidden, rather than
     * emptied: the work is still there if the property is opened again, and
     * the discard guard still asks before a reload throws it away. Every other
     * page is unmounted, which stops its reads, and mounts afresh if the
     * property comes back — nothing it read in the meantime survives.
     */
    holdsPage: boolean;
    /** Ask the service again — once the tab's own property has been opened again. */
    check: () => void;
}

/**
 * The shell's watch over the property its pages were loaded for.
 *
 * The open property is server-side state every tab, window and device of a
 * user shares, and a page reads it once. A tab looked at again after another
 * one opened a second property went on showing the first property's data
 * while every read it made from then on answered for the second — the strategy
 * editor showed BH Test Eda's strategy over Art Hotel's room types, with no
 * room type left (30 Sep 2026) — and wrote to the second under the first one's
 * name. So the shell asks `/api/me` whenever the tab is looked at and whenever
 * something asks it to (`openPropertyEvents.ts`: a change refused for naming a
 * property no longer open, a page about to read the open property), and while
 * another property is open, no page that shows one is drawn.
 *
 * It is also what tells the Prices page's changes which property to name
 * (`withPageConnection`): the one the shell loaded the page on screen for,
 * pinned to that page's mount. A switch made in this tab names the new
 * property to the shell before the pages load again for it
 * (`useDashboardInit`), and a change from the page still on screen meanwhile
 * must name the property that page shows, or it would be written onto the one
 * being opened.
 */
export function useOpenPropertyWatch({
    loadedConnectionId,
    connectionVersion,
    page,
    showsProperty = true,
    loadedAccess,
    onAccessChanged,
}: OpenPropertyWatchOptions): OpenPropertyWatch {
    const { changed, openConnectionId, check, openAccess } = useOpenPropertyCheck(loadedConnectionId);

    // Compared when an answer arrives, never when the shell's own access
    // moves: opening another property names its access before `/api/me` has
    // named the property, and the last answer, still the old property's, is
    // no news about it. A change is told once, however many answers repeat
    // it — the person may have chosen to stay with unsaved work — and forgot
    // once an answer agrees with what the pages were loaded with.
    const loadedAccessRef = useRef(loadedAccess);
    loadedAccessRef.current = loadedAccess;
    const onAccessChangedRef = useRef(onAccessChanged);
    onAccessChangedRef.current = onAccessChanged;
    const toldRef = useRef<string | null>(null);
    useEffect(() => {
        const loaded = loadedAccessRef.current;
        if (!openAccess || !loaded) return;
        const answer = gatingReadingOf(openAccess);
        if (answer === gatingReadingOf(loaded)) {
            toldRef.current = null;
            return;
        }
        if (toldRef.current === answer) return;
        toldRef.current = answer;
        const tell = onAccessChangedRef.current;
        // Taking the new access remounts every page, so a page holding unsaved
        // work asks first, as a property switch does.
        if (tell) runWhenUnblocked(tell);
    }, [openAccess]);

    useEffect(() => onOpenPropertyCheckRequested(check), [check]);

    // The property the page on screen was loaded for: the shell's, taken again
    // whenever the pages are loaded again, and as soon as `/api/me` first names
    // one.
    const [pinned, setPinned] = useState({ version: connectionVersion, connectionId: loadedConnectionId });
    if (
        pinned.version !== connectionVersion
        || (pinned.connectionId === undefined && loadedConnectionId !== undefined)
    ) {
        setPinned({ version: connectionVersion, connectionId: loadedConnectionId });
    }
    useEffect(() => {
        setPageConnection(pinned.connectionId);
        return () => setPageConnection(undefined);
    }, [pinned.connectionId]);

    const openElsewhere = changed && page !== null && !isAdminPage(page) && showsProperty;

    // Whether the page is kept, decided the moment the notice takes its place
    // — the guard is asked then, never on every render — and again for a page
    // reached from the rail while it stays.
    const [held, setHeld] = useState<{ page: PageName; hold: boolean } | null>(null);
    if (openElsewhere && page !== null && held?.page !== page) {
        setHeld({ page, hold: hasUnsavedWork() });
    } else if (!openElsewhere && held !== null) {
        setHeld(null);
    }
    const holdsPage = openElsewhere && held !== null && held.page === page && held.hold;

    // What the tab holds about a property — the room kinds' order, the price
    // recalculation the Prices page waits on — may have been filled with the
    // other property's in the moment before the answer came.
    useEffect(() => {
        if (changed) forgetPropertyCaches();
    }, [changed]);

    return { changed, openElsewhere, openConnectionId, holdsPage, check };
}

/**
 * An access as the shell gates pages by it: full access (an owner and a
 * member open the same pages, and the server refuses an owner's write to a
 * member whatever the screen shows), a viewer of either kind, staff, or none.
 */
function gatingReadingOf(access: OpenPropertyAccess): string {
    switch (access.role) {
        case "owner":
        case "member":
            return "full";
        case "viewer":
            return `viewer:${access.viewerKind ?? "management"}`;
        case "staff":
            return "staff";
        default:
            return "none";
    }
}
